import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { auth, db, SESSION_TIMEOUT_MS, INACTIVITY_TIMEOUT_MS } from '../firebase/firebaseConfig';
import { signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ═══ SESSION & INACTIVITY TIMEOUT REFS ═══
  const sessionTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  const SESSION_LOGIN_KEY = 'rg_session_login_ts';

  // Force logout helper
  const forceLogout = useCallback(async (reason) => {
    console.log(`🔒 Auto-logout triggered: ${reason}`);
    try {
      await signOut(auth);
      sessionStorage.removeItem(SESSION_LOGIN_KEY);
      localStorage.removeItem('user');
      sessionStorage.clear();
    } catch (err) {
      console.error('Auto-logout error:', err);
    }
  }, []);

  // Reset the inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (!auth.currentUser) return; // No user, nothing to track
    inactivityTimerRef.current = setTimeout(() => {
      forceLogout('Inactivity timeout (' + Math.round(INACTIVITY_TIMEOUT_MS / 60000) + ' min)');
    }, INACTIVITY_TIMEOUT_MS);
  }, [forceLogout]);

  // Start session-absolute timer (from login time)
  const startSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);

    // Read stored login timestamp
    const loginTs = Number(sessionStorage.getItem(SESSION_LOGIN_KEY));
    if (!loginTs) {
      // No login timestamp means fresh login — set it now
      sessionStorage.setItem(SESSION_LOGIN_KEY, Date.now().toString());
    }
    const elapsed = Date.now() - (loginTs || Date.now());
    const remaining = SESSION_TIMEOUT_MS - elapsed;

    if (remaining <= 0) {
      // Session already expired (e.g. tab was kept open past timeout)
      forceLogout('Session expired (' + Math.round(SESSION_TIMEOUT_MS / 60000) + ' min)');
      return;
    }

    sessionTimerRef.current = setTimeout(() => {
      forceLogout('Session expired (' + Math.round(SESSION_TIMEOUT_MS / 60000) + ' min)');
    }, remaining);

    console.log(`⏱️ Session timer: ${Math.round(remaining / 60000)} min remaining`);
  }, [forceLogout]);

  // ═══ ACTIVITY LISTENERS (inactivity timeout) ═══
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    const handleActivity = () => resetInactivityTimer();

    activityEvents.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, [resetInactivityTimer]);

  // ═══ AUTH STATE LISTENER ═══
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await fetchUserData(user.uid);
        // Start both timers on auth
        startSessionTimer();
        resetInactivityTimer();
      } else {
        setUserData(null);
        setLoading(false);
        // Clean up timers
        if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        sessionStorage.removeItem(SESSION_LOGIN_KEY);
      }
    });

    return () => unsubscribe();
  }, [startSessionTimer, resetInactivityTimer]);

  // Helper function to check if value is unlimited
  const isUnlimited = (value) => value === -1 || value === 'unlimited';

  // Validate and update plan status (same logic as ProfilePage)
  const validateAndUpdatePlanStatus = async (uid, userData) => {
    try {
      if (!userData) return userData;

      let needsUpdate = false;
      const updates = {};

      // Check if plan has expired (ONLY date-based — limits do NOT deactivate the plan)
      if (userData.planExpiryDate && userData.isActivePlan) {
        const expiryDate = userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate);
        const currentDate = new Date();
        
        // Expiry day itself is still valid — only expire after end of expiry day
        const expiryEndOfDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate(), 23, 59, 59);
        
        if (currentDate > expiryEndOfDay) {
          console.log('Plan has expired (date passed). Deactivating plan...');
          updates.isActivePlan = false;
          updates.planType = 'free';
          updates.resumeDownloadLimit = 0;
          updates.coverLetterDownloadLimit = 0;
          updates.atsCheckLimit = 5;
          updates.hasAICoaching = false;
          updates.hasUnlimitedStorage = false;
          updates.hasMockInterview = false;
          updates.hasHRConsultancy = false;
          // NOTE: Do NOT reset resumeDownloadsUsed / coverLetterDownloadsUsed here.
          // Counts are only reset after a NEW plan payment is made.
          needsUpdate = true;
        }
      }

      // Update Firestore if needed
      if (needsUpdate) {
        const userDocRef = doc(db, 'users', uid);
        await updateDoc(userDocRef, {
          ...updates,
          updatedAt: new Date()
        });
        
        console.log('Plan status updated in Firestore:', updates);
        
        // Return updated user data
        return {
          ...userData,
          ...updates
        };
      }

      return userData;
    } catch (error) {
      console.error('Error validating plan status:', error);
      return userData;
    }
  };

  const fetchUserData = async (uid) => {
    try {
      setLoading(true);
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Validate plan status before setting user data
        const validatedData = await validateAndUpdatePlanStatus(uid, data);
        
        // Store essential user data with validated values
        setUserData({
          uid: uid,
          email: validatedData.email || auth.currentUser?.email,
          displayName: validatedData.displayName || auth.currentUser?.displayName,
          photoURL: validatedData.photoURL || auth.currentUser?.photoURL,
          mobileNumber: validatedData.mobileNumber || '',
          planType: validatedData.planType || 'free',
          isActivePlan: validatedData.isActivePlan || false,
          downloadLimit: validatedData.downloadLimit || 0, // Legacy field
          resumeDownloadLimit: validatedData.resumeDownloadLimit || 0,
          coverLetterDownloadLimit: validatedData.coverLetterDownloadLimit || 0,
          atsCheckLimit: validatedData.atsCheckLimit || 5,
          resumeDownloadsUsed: validatedData.resumeDownloadsUsed || 0,
          coverLetterDownloadsUsed: validatedData.coverLetterDownloadsUsed || 0,
          atsChecksUsed: validatedData.atsChecksUsed || 0,
          planExpiryDate: validatedData.planExpiryDate || null,
          hasMockInterview: validatedData.hasMockInterview || false,
          hasHRConsultancy: validatedData.hasHRConsultancy || false,
          hasAICoaching: validatedData.hasAICoaching || false,
          hasUnlimitedStorage: validatedData.hasUnlimitedStorage || false,
          createdAt: validatedData.createdAt,
          updatedAt: validatedData.updatedAt
        });
      } else {
        // User document doesn't exist - create new user with defaults
        console.log('New user detected, creating default user data...');
        const defaultUserData = {
          uid: uid,
          email: auth.currentUser?.email,
          displayName: auth.currentUser?.displayName,
          photoURL: auth.currentUser?.photoURL,
          mobileNumber: '',
          planType: 'free',
          isActivePlan: false,
          downloadLimit: 0,
          resumeDownloadLimit: 0,
          coverLetterDownloadLimit: 0,
          atsCheckLimit: 5,
          resumeDownloadsUsed: 0,
          coverLetterDownloadsUsed: 0,
          atsChecksUsed: 0,
          planExpiryDate: null,
          hasMockInterview: false,
          hasHRConsultancy: false,
          hasAICoaching: false,
          hasUnlimitedStorage: false,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // Create user document in Firestore
        await setDoc(userDocRef, defaultUserData);
        console.log('New user document created in Firestore');

        setUserData(defaultUserData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Set minimal user data on error
      setUserData({
        uid: uid,
        planType: 'free',
        isActivePlan: false,
        downloadLimit: 0,
        resumeDownloadLimit: 0,
        coverLetterDownloadLimit: 0,
        planExpiryDate: null
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    if (auth.currentUser) {
      await fetchUserData(auth.currentUser.uid);
    }
  };

  const value = {
    userData,
    loading,
    refreshUserData
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
